import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { ManagerRegistry } from '../../core/manager-registry';
import type { RoamingPoint } from './types';

/**
 * 路径漫游面板组件
 * 提供漫游点的添加、删除、跳转和播放功能
 */
export class WalkPathPanel implements IBimComponent {
    public element!: HTMLElement;

    /** 管理器注册表実例 */
    private registry: ManagerRegistry;

    /** 管理器注册表实例 */
    /** 国际化订阅取消函数 */
    private unsubscribeLocale: (() => void) | null = null;
    /** 主题订阅取消函数 */
    private unsubscribeTheme: (() => void) | null = null;

    constructor(registry: ManagerRegistry) {
        this.registry = registry;
    }


    /** 漫游点列表 */
    private points: RoamingPoint[] = [];
    /** 漫游时间（毫秒） */
    private duration: number = 10000;
    /** 是否循环播放 */
    private loop: boolean = false;
    /** 当前播放中的点索引，-1 表示未播放 */
    private playingPointIndex: number = -1;
    /** 是否正在播放 */
    private isPlaying: boolean = false;

    /**
     * 初始化组件
     * 创建 DOM 元素，订阅国际化和主题变化，加载已有漫游点
     */
    public init(): void {
        // 创建根元素
        this.element = document.createElement('div');
        this.element.className = 'walk-path-panel';

        // 订阅国际化变化
        this.unsubscribeLocale = localeManager.subscribe(() => this.render());
        // 订阅主题变化
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));

        // 从引擎加载已有的漫游点
        this.loadPointsFromEngine();

        // 渲染界面
        this.render();
        // 应用当前主题
        this.setTheme(themeManager.getTheme());
    }

    /**
     * 从引擎加载已有的漫游点
     * 在面板打开时调用，获取底层引擎中已存在的漫游点
     */
    private loadPointsFromEngine(): void {
        const enginePoints = this.registry.engine3d?.getEngineComponent()?.pathRoamingGetPoints() ?? [];
        // 将引擎返回的点转换为本地格式
        this.points = enginePoints.map((_: any, index: number) => ({
            index: index
        }));
    }

    /**
     * 渲染整个面板
     * 清空现有内容并重新构建界面
     */
    private render(): void {
        this.element.innerHTML = '';

        // 渲染路径设置区域
        const settings = this.createSettingsSection();
        this.element.appendChild(settings);

        // 渲染漫游点区域
        const pointsSection = this.createPointsSection();
        this.element.appendChild(pointsSection);

        // 渲染播放按钮
        const playBtn = this.createPlayButton();
        this.element.appendChild(playBtn);
    }

    /**
     * 创建路径设置区域
     * 包含漫游时间和循环播放设置
     */
    private createSettingsSection(): HTMLElement {
        const section = document.createElement('div');
        section.className = 'walk-path-settings';

        // ===== 漫游时间 =====
        const durationGroup = document.createElement('div');
        durationGroup.className = 'walk-path-form-group';

        const durationLabel = document.createElement('label');
        durationLabel.textContent = t('walkControl.path.duration');

        const durationWrapper = document.createElement('div');
        durationWrapper.className = 'walk-path-input-wrapper';

        const durationInput = document.createElement('input');
        durationInput.type = 'number';
        durationInput.className = 'walk-path-input';
        durationInput.value = String(this.duration / 1000);
        durationInput.min = '1';
        durationInput.oninput = (e) => {
            // 更新漫游时间，转换为毫秒
            const val = parseInt((e.target as HTMLInputElement).value) || 1;
            this.duration = val * 1000;
        };

        const durationUnit = document.createElement('span');
        durationUnit.className = 'walk-path-unit';
        durationUnit.textContent = t('walkControl.path.durationUnit');

        durationWrapper.appendChild(durationInput);
        durationWrapper.appendChild(durationUnit);
        durationGroup.appendChild(durationLabel);
        durationGroup.appendChild(durationWrapper);

        // ===== 循环播放 =====
        const loopGroup = document.createElement('div');
        loopGroup.className = 'walk-path-form-group walk-path-form-group-inline';

        const loopCheckbox = document.createElement('input');
        loopCheckbox.type = 'checkbox';
        loopCheckbox.id = 'walk-path-loop-checkbox';
        loopCheckbox.className = 'walk-path-checkbox';
        loopCheckbox.checked = this.loop;
        loopCheckbox.onchange = (e) => {
            // 更新循环播放状态
            this.loop = (e.target as HTMLInputElement).checked;
        };

        const loopLabel = document.createElement('label');
        loopLabel.htmlFor = 'walk-path-loop-checkbox';
        loopLabel.textContent = t('walkControl.path.loop');

        loopGroup.appendChild(loopCheckbox);
        loopGroup.appendChild(loopLabel);

        section.appendChild(durationGroup);
        section.appendChild(loopGroup);

        return section;
    }

    /**
     * 创建漫游点区域
     * 包含操作按钮和漫游点列表
     */
    private createPointsSection(): HTMLElement {
        const section = document.createElement('div');
        section.className = 'walk-path-points-section';

        // ===== 操作栏 =====
        const toolbar = document.createElement('div');
        toolbar.className = 'walk-path-points-toolbar';

        // 添加漫游点按钮
        const addBtn = document.createElement('button');
        addBtn.className = 'walk-path-btn walk-path-btn-small';
        addBtn.textContent = `+ ${t('walkControl.path.addPoint')}`;
        addBtn.onclick = () => this.addPoint();

        // 删除全部按钮
        const deleteAllBtn = document.createElement('button');
        deleteAllBtn.className = 'walk-path-btn walk-path-btn-small walk-path-btn-danger';
        deleteAllBtn.textContent = t('walkControl.path.deleteAll');
        deleteAllBtn.onclick = () => this.deleteAllPoints();

        toolbar.appendChild(addBtn);
        toolbar.appendChild(deleteAllBtn);
        section.appendChild(toolbar);

        // ===== 漫游点列表 =====
        const list = document.createElement('div');
        list.className = 'walk-path-points-list';

        if (this.points.length === 0) {
            // 空状态提示
            const empty = document.createElement('div');
            empty.className = 'walk-path-empty';
            empty.textContent = t('walkControl.path.noPoints');
            list.appendChild(empty);
        } else {
            // 渲染每个漫游点
            this.points.forEach((_, index) => {
                const item = this.createPointItem(index);
                list.appendChild(item);
            });
        }

        section.appendChild(list);
        return section;
    }

    /**
     * 创建单个漫游点项
     * @param index 漫游点索引
     */
    private createPointItem(index: number): HTMLElement {
        const item = document.createElement('div');
        item.className = 'walk-path-point-item';
        // 如果是当前播放的点，添加高亮样式
        if (this.playingPointIndex === index) {
            item.classList.add('walk-path-point-item-active');
        }

        // 漫游点名称
        const name = document.createElement('span');
        name.className = 'walk-path-point-name';
        name.textContent = `${t('walkControl.path.point')}${index}`;

        // 操作按钮容器
        const actions = document.createElement('div');
        actions.className = 'walk-path-point-actions';

        // 跳转按钮
        const jumpBtn = document.createElement('button');
        jumpBtn.className = 'walk-path-btn-icon';
        jumpBtn.innerHTML = '▶';
        jumpBtn.title = t('walkControl.path.play');
        jumpBtn.onclick = (e) => {
            e.stopPropagation();
            this.jumpToPoint(index);
        };

        // 删除按钮
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'walk-path-btn-icon walk-path-btn-icon-danger';
        deleteBtn.innerHTML = '×';
        deleteBtn.onclick = (e) => {
            e.stopPropagation();
            this.deletePoint(index);
        };

        actions.appendChild(jumpBtn);
        actions.appendChild(deleteBtn);

        item.appendChild(name);
        item.appendChild(actions);
        return item;
    }

    /**
     * 创建播放/停止按钮组
     */
    private createPlayButton(): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'walk-path-btn-group';

        const playBtn = document.createElement('button');
        playBtn.className = 'walk-path-btn walk-path-btn-play';
        playBtn.textContent = `▶ ${t('walkControl.path.play')}`;
        playBtn.disabled = this.points.length === 0 || this.isPlaying;
        playBtn.onclick = () => this.playPath();

        const stopBtn = document.createElement('button');
        stopBtn.className = 'walk-path-btn walk-path-btn-stop';
        stopBtn.textContent = `■ ${t('walkControl.path.stop')}`;
        stopBtn.disabled = !this.isPlaying;
        stopBtn.onclick = () => this.stopPath();

        wrapper.appendChild(playBtn);
        wrapper.appendChild(stopBtn);
        return wrapper;
    }

    // ==================== 操作方法 ====================

    /**
     * 添加漫游点
     * 将当前相机位置添加为新的漫游点
     */
    private addPoint(): void {
        // 调用引擎添加漫游点
        this.registry.engine3d?.getEngineComponent()?.pathRoamingAddPoint();
        // 更新本地状态
        const newIndex = this.points.length;
        this.points.push({ index: newIndex });
        // 重新渲染
        this.render();
    }

    /**
     * 删除指定漫游点
     * @param index 要删除的漫游点索引
     */
    private deletePoint(index: number): void {
        // 调用引擎删除漫游点
        this.registry.engine3d?.getEngineComponent()?.pathRoamingRemovePoint(index);
        // 从本地列表中移除
        this.points.splice(index, 1);
        // 重新索引
        this.points.forEach((p, i) => p.index = i);
        // 重新渲染
        this.render();
    }

    /**
     * 删除所有漫游点
     */
    private deleteAllPoints(): void {
        // 调用引擎清除所有漫游点
        this.registry.engine3d?.getEngineComponent()?.pathRoamingClearPoints();
        // 清空本地列表
        this.points = [];
        // 重新渲染
        this.render();
    }

    /**
     * 跳转到指定漫游点
     * @param index 目标漫游点索引
     */
    private jumpToPoint(index: number): void {
        this.registry.engine3d?.getEngineComponent()?.pathRoamingJumpToPoint(index);
    }

    /**
     * 播放漫游
     * 按顺序播放所有漫游点
     */
    private playPath(): void {
        if (this.points.length === 0) return;

        this.isPlaying = true;
        this.render();

        console.log('[WalkPathPanel] 开始播放漫游', { duration: this.duration, loop: this.loop, pointsCount: this.points.length });

        this.registry.engine3d?.getEngineComponent()?.pathRoamingPlay({
            duration: this.duration,
            loop: this.loop,
            onPointComplete: (pointIndex: number) => {
                console.log('[WalkPathPanel] onPointComplete', { pointIndex });
                this.playingPointIndex = pointIndex;
                this.render();
            },
            onComplete: () => {
                console.log('[WalkPathPanel] onComplete 播放完成');
                this.isPlaying = false;
                this.playingPointIndex = -1;
                this.render();
            }
        });
    }

    /**
     * 停止漫游
     */
    private stopPath(): void {
        console.log('[WalkPathPanel] 停止漫游');
        this.registry.engine3d?.getEngineComponent()?.pathRoamingStop();
        this.isPlaying = false;
        this.playingPointIndex = -1;
        this.render();
    }

    // ==================== 生命周期 ====================

    /**
     * 更新国际化文本
     * 当语言切换时重新渲染整个面板
     */
    public setLocales(): void {
        // 重新渲染以更新所有文本
        this.render();
    }

    /**
     * 应用主题
     * @param theme 主题配置
     */
    public setTheme(theme: ThemeConfig): void {
        if (!this.element) return;
        // 设置 CSS 变量
        this.element.style.setProperty('--bim-text-primary', theme.textPrimary ?? '#fff');
        this.element.style.setProperty('--bim-text-secondary', theme.textPrimary ?? '#fff');
        this.element.style.setProperty('--bim-bg-elevated', theme.bgElevated ?? '#1f2d3e');
        this.element.style.setProperty('--bim-border-default', theme.borderDefault ?? '#334155');
        this.element.style.setProperty('--bim-primary', theme.primary ?? '#3b82f6');
    }

    /**
     * 销毁组件
     * 清理订阅和 DOM 元素
     */
    public destroy(): void {
        // 如果正在播放，先停止
        if (this.isPlaying) {
            this.stopPath();
        }
        // 取消订阅
        this.unsubscribeLocale?.();
        this.unsubscribeTheme?.();
        // 移除 DOM 元素
        if (this.element?.parentElement) {
            this.element.parentElement.removeChild(this.element);
        }
    }
}
